/* eslint-disable jsx-a11y/click-events-have-key-events */
import axios from 'axios';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Demo from './SkeletonDemo';

import Constants from '../../Constants';
import { tr } from '../utils/locale';
import markerIcon from '../../images/marker-dot.svg';
import markerGeolocationIcon from '../../images/marker-alt.png';
import Input from '../Input/Input';

import { WoosmapMapBoundingBox, woosmapBoundsFromViewport, createWoosmapMap } from '../Map/drawOnMap';

export default class GeolocationDemo extends Component {
    constructor(props) {
        super(props);
        this.state = {
            geolocation: null,
            error: null,
            showStores: false,
            hasStores: false,
        };
        this.map = null;
        this.marker = null;
        this.viewport = null;
        this.requestUrl = 'https://api.woosmap.com/geolocation/stores';
        this.demoRef = React.createRef();
        this.overlay = null;
        this.storeMarkers = [];
    }

    componentDidMount() {
        this.displayMap();
    }

    requestGeolocation = () => {
        axios
            .get(this.requestUrl, {
                params: this.getRequestparams(),
            })
            .then((response) => {
                const { data: geolocation } = response;
                const { stores } = geolocation;
                const hasStores = stores?.features?.length > 1;

                this.setState({ geolocation, hasStores, error: null }, this.displayOnMap);
            })
            .catch((error) => {
                const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                this.setState({ error: errorResult });
            });
    };

    // eslint-disable-next-line class-methods-use-this
    getRequestparams = () => ({
        key: Constants.geolocationWoosmapSearchKey,
        radius: 200000,
        limit: 20,
    });

    processMarker = (latLng, icon, forceMap = null, scaledSize = { height: 24, width: 24 }) => {
        const { showStores } = this.state;

        let map;
        if (!forceMap) map = showStores ? this.map : null;
        else map = forceMap;

        return new window.woosmap.map.Marker({ icon: { url: icon, scaledSize }, position: latLng, map });
    };

    displayOnMap = () => {
        const { geolocation } = this.state;

        if (this.map && geolocation) {
            const location = new window.woosmap.map.LatLng(geolocation.latitude, geolocation.longitude);

            if (this.marker) {
                this.marker.setMap(null);
                this.marker = null;
            }

            if (this.storeMarkers) {
                this.storeMarkers.forEach((store) => store.setMap(null));
                this.storeMarkers = [];
            }

            if (this.overlay) {
                this.overlay.setMap(null);
                this.overlay = null;
            }

            if (geolocation?.stores?.features) {
                geolocation.stores.features.forEach((feature) => {
                    const [lng, lat] = feature.geometry.coordinates;
                    this.storeMarkers.push(this.processMarker(new window.woosmap.map.LatLng({ lat, lng }), markerIcon));
                });
            }

            this.marker = this.processMarker(location, markerGeolocationIcon, this.map, { height: 46, width: 30 });

            const bounds = woosmapBoundsFromViewport(geolocation.viewport);

            this.overlay = new WoosmapMapBoundingBox(bounds, { top: 55, left: 30, bottom: 10, right: 30 });
            this.overlay.setMap(this.map);

            this.map.fitBounds(bounds, { top: 70, left: 70, bottom: 70, right: 70 });
        }
    };

    renderFooterFilters = () => {
        const { hasStores, showStores } = this.state;

        if (!hasStores) {
            return [];
        }

        const filterStores = {
            label: '',
            key: 'stores',
            component: (
                <Input
                    label={tr('Display Geolocation POIs')}
                    type="checkbox"
                    checked={showStores}
                    onChange={(e) => {
                        this.setState({ showStores: e.target.checked }, this.displayMap);
                    }}
                />
            ),
        };

        return [filterStores];
    };

    renderGeolocationInfo = () => {
        const { geolocation } = this.state;
        if (!geolocation) {
            return <div className="demo__empty">No geolocation found</div>;
        }
        const result = [];
        [
            { id: 'timezone', label: tr('Timezone') },
            { id: 'continent', label: tr('Continent') },
            { id: 'country_name', label: tr('Country') },
            { id: 'region_state', label: tr('Region / State') },
            { id: 'postal_code', label: tr('Postal code') },
        ].forEach((item) => {
            if (geolocation[item.id]) {
                result.push(
                    <li key={item.id}>
                        {item.label}:&nbsp;
                        <strong>{geolocation[item.id]}</strong>
                    </li>
                );
            }
            return null;
        });
        result.push(
            <li key="location">
                {tr('Location')}:&nbsp;
                <strong>
                    {geolocation.latitude}, {geolocation.longitude}
                </strong>
            </li>
        );
        result.push(
            <li key="accuracy">
                {tr('Accuracy')}:&nbsp;
                <strong>{geolocation.accuracy}km</strong>
            </li>
        );

        return <ul>{result}</ul>;
    };

    displayMap = () => {
        if (this.timeoutMap) {
            clearTimeout(this.timeoutMap);
        }

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.geolocationWoosmapSearchKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
            this.requestGeolocation();
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    render() {
        const { geolocation, error } = this.state;
        const { noheader } = this.props;
        const headerLabels = {
            title: tr('Geolocation API'),
            desc: tr(
                "Automatically retrieve and analyse your user's location thanks to the advanced Maxmind IP database and list your nearby points of sale."
            ),
        };
        let precisionInfo = null;
        if (geolocation) {
            precisionInfo = tr(
                'Identify the country to redirect users to the suitable website, or adapt your services according to the timezone.'
            );
            if (geolocation.accuracy <= 20) {
                precisionInfo = tr(
                    'Initialise your map display around the end user and retrieve your own Points of Interest.'
                );
            } else if (geolocation.accuracy <= 200) {
                precisionInfo = tr('Display your regional promotions and catchment area.');
            }
        }
        return (
            <Demo
                className="demo--geolocation"
                docLink="https://developers.woosmap.com/products/geolocation-api/location/"
                id="geolocation-demo"
                header={headerLabels}
                headerTagline={precisionInfo}
                request={this.requestUrl}
                params={this.getRequestparams()}
                referer="http://localhost/"
                noheader={noheader}
                response={error || geolocation}
                result={this.renderGeolocationInfo()}
                withMap
                footerFilters={this.renderFooterFilters()}
                ref={this.demoRef}
            />
        );
    }
}

GeolocationDemo.defaultProps = {
    noheader: false,
};
GeolocationDemo.propTypes = {
    noheader: PropTypes.bool,
};
